import { IdentidadeFeed } from '../types/identidadeFeed';
import { Organizador } from '../services/torneioService';
import { CurrentProfile } from '../types/profile';

export interface ComputeIdentidadesParams {
  torneio: {
    id: string;
    nome: string;
    avatarUrl?: string | null;
    organizadores?: Organizador[] | null;
    liga?: { id: string; nome: string; imagemUrl?: string | null } | null;
  };
  userId: string | null;
  atletaId: string | null;
  atletaNome: string | null;
  atletaImagemUrl?: string | null;
  isAdmin: boolean;
  userOrganizadores?: Array<{
    liga?: { id: string } | null;
    torneio?: { id: string } | null;
  }> | null;
}

export interface IdentidadesResult {
  identidades: IdentidadeFeed[];
  canPost: boolean;
}

const FUNCOES_SOCIAL = ['SOCIAL', 'ADMINISTRADOR'];

export function computeIdentidadesFeed(params: ComputeIdentidadesParams): IdentidadesResult {
  const {
    torneio,
    userId,
    atletaId,
    atletaNome,
    atletaImagemUrl,
    isAdmin,
    userOrganizadores,
  } = params;

  const identidades: IdentidadeFeed[] = [];
  let canPost = false;

  const orgDoUsuario = userId
    ? torneio.organizadores?.find((org) => org.usuario?.id === userId)
    : null;

  const isFuncaoPermitida = orgDoUsuario
    ? FUNCOES_SOCIAL.includes(orgDoUsuario.nomeFuncao ?? '')
    : false;

  if (isFuncaoPermitida || isAdmin) {
    canPost = true;
    identidades.push({
      tipo: 'TORNEIO',
      id: torneio.id,
      nome: torneio.nome,
      imagemUrl: torneio.avatarUrl ?? null,
    });
  }

  if (torneio.liga) {
    const isLigaOrganizer = userOrganizadores?.some(
      (org) => org.liga?.id === torneio.liga!.id
    );
    if (isLigaOrganizer) {
      identidades.push({
        tipo: 'LIGA',
        id: torneio.liga.id,
        nome: torneio.liga.nome,
        imagemUrl: torneio.liga.imagemUrl ?? null,
      });
    }
  }

  if (atletaId && atletaNome) {
    identidades.push({
      tipo: 'ATLETA',
      id: atletaId,
      nome: atletaNome,
      imagemUrl: atletaImagemUrl ?? null,
    });
  }

  return { identidades, canPost };
}

export function resolverIdsParaMutacao(identidade: IdentidadeFeed): {
  autorAtletaId: string;
  autorTorneioId: string;
  autorLigaId: string;
} {
  return {
    autorAtletaId: identidade.tipo === 'ATLETA' ? identidade.id : '',
    autorTorneioId: identidade.tipo === 'TORNEIO' ? identidade.id : '',
    autorLigaId: identidade.tipo === 'LIGA' ? identidade.id : '',
  };
}

/**
 * Retorna apenas o campo da entidade selecionada. Os demais ficam undefined.
 * Usar em mutations que aceitam campos opcionais (ex: REMOVER_CURTIDA).
 */
export function resolverIdExclusivoParaMutacao(identidade: IdentidadeFeed): {
  autorAtletaId?: string;
  autorTorneioId?: string;
  autorLigaId?: string;
} {
  return {
    autorAtletaId: identidade.tipo === 'ATLETA' ? identidade.id : undefined,
    autorTorneioId: identidade.tipo === 'TORNEIO' ? identidade.id : undefined,
    autorLigaId: identidade.tipo === 'LIGA' ? identidade.id : undefined,
  };
}

/**
 * Converte o perfil ativo (`ProfileContext.currentProfile`) numa `IdentidadeFeed`.
 *
 * Ponto único de derivação: os componentes de post NÃO consomem o contexto por
 * conta própria — as pages derivam e passam por prop, conforme
 * `ai/anti-patterns.md` §1 ("Data Fetching Inside Reusable Components").
 */
export function identidadeDoPerfilAtivo(
  currentProfile: CurrentProfile | null | undefined,
  fallbackAtleta?: { id: string; nome: string; imagemUrl?: string | null } | null,
): IdentidadeFeed | null {
  if (currentProfile) {
    return {
      tipo: currentProfile.tipo,
      id: currentProfile.referenciaId,
      nome: currentProfile.nome,
      imagemUrl: currentProfile.imagemUrl ?? null,
    };
  }

  if (fallbackAtleta) {
    return {
      tipo: 'ATLETA',
      id: fallbackAtleta.id,
      nome: fallbackAtleta.nome,
      imagemUrl: fallbackAtleta.imagemUrl ?? null,
    };
  }

  return null;
}

/**
 * Escolhe a identidade pré-selecionada de uma lista, priorizando a que
 * corresponde ao perfil ativo. Só cai no primeiro item quando nenhuma casa —
 * preservando o comportamento anterior para quem não informa perfil ativo.
 */
export function escolherIdentidadePadrao(
  identidades: IdentidadeFeed[] | null | undefined,
  currentProfile: CurrentProfile | null | undefined,
): IdentidadeFeed | null {
  if (!identidades || identidades.length === 0) {
    return null;
  }

  const alvo = identidadeDoPerfilAtivo(currentProfile);
  if (alvo) {
    const casada = identidades.find(
      (identidade) => identidade.tipo === alvo.tipo && identidade.id === alvo.id,
    );
    if (casada) {
      return casada;
    }
  }

  return identidades[0];
}
